import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Platform } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import LHOverlay from '../../../components/LHOverlay';
import LHModalPop from '../../../components/LHModalPop';
import FontFamily from '../../../constants/fonts';
import { useSparcKpiFavorite } from '../hooks/useSparcKpiFavorite';
import { getUnfavouriteModalConfig } from '../constants/unfavouriteModalConfig';
import { useBarHoverTooltip } from './BarHoverTooltip';

interface SparcKpiDetailModalProps {
  visible: boolean;
  onDismiss: () => void;
  onCreateActionPlan?: () => void;
  cardData: {
    id: string;
    title: string;
    badgeLetter: string;
    badgeColor?: string;
    isMandatory?: boolean;
    actual: number;
    target: number;
    delta: number;
    invertDeltaColor?: boolean;
    chartData: Array<{ date: string; value: number }>;
  } | null;
  isFavorite?: boolean;
  isMandatory?: boolean;
  onToggleFavorite?: () => void;
}

const StarIcon = ({ filled = false, isLoading = false }: { filled?: boolean; isLoading?: boolean }) => {
  if (isLoading) {
    return (
      <View style={styles.starIconContainer}>
        <MaterialIcons 
          name="autorenew" 
          size={18} 
          color="#616161"
          style={Platform.OS === 'web' ? ({
            animation: 'spin 1s linear infinite',
          } as any) : undefined}
        />
      </View>
    );
  }
  
  return (
    <View style={styles.starIconContainer}>
      <Text style={[styles.starIcon, filled && { color: '#2D2D2D' }]}>
        {filled ? '★' : '☆'}
      </Text>
    </View>
  );
};

const DownArrowIcon = ({ color }: { color: string }) => (
  <View style={styles.downArrowContainer}>
    <Text style={[styles.downArrow, { color }]}>▼</Text>
  </View>
);

const UpArrowIcon = ({ color }: { color: string }) => (
  <View style={styles.upArrowContainer}>
    <Text style={[styles.upArrow, { color }]}>▲</Text>
  </View>
);

const SparcKpiDetailModal: React.FC<SparcKpiDetailModalProps> = ({
  visible,
  onDismiss,
  onCreateActionPlan,
  cardData,
  isFavorite = false,
  isMandatory = false,
  onToggleFavorite,
}) => {
  const [isStarFilled, setIsStarFilled] = useState(isFavorite);
  const [showUnfavouriteModal, setShowUnfavouriteModal] = useState(false);
  const [pendingFavoriteAction, setPendingFavoriteAction] = useState<{ action: 'favorite' | 'unfavorite', cardData: any } | null>(null);
  const { toggleFavorite, isLoading } = useSparcKpiFavorite();
  const { getBarProps, tooltip } = useBarHoverTooltip();

  useEffect(() => {
    setIsStarFilled(isFavorite);
  }, [isFavorite]);

  // Clear confirmation modal when main modal closes
  useEffect(() => {
    if (!visible) {
      setShowUnfavouriteModal(false);
      setPendingFavoriteAction(null);
    }
  }, [visible]);

  // Inject CSS animation for spinning icon on web
  useEffect(() => {
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const styleId = 'sparc-kpi-modal-spinner-animation';
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `;
        document.head.appendChild(style);
      }
    }
  }, []);

  const handleStarPress = async () => {
    if (isLoading || !cardData || isMandatory) return;
    
    const willBeUnfavorited = isStarFilled; // Currently starred, so clicking will unstar
    
    if (willBeUnfavorited) {
      // Show confirmation modal for unfavorite action
      setPendingFavoriteAction({ action: 'unfavorite', cardData });
      setShowUnfavouriteModal(true);
    } else {
      // Directly favorite without confirmation
      setIsStarFilled(true);
      if (onToggleFavorite) {
        onToggleFavorite();
      }
      const success = await toggleFavorite(cardData.id, cardData.title);
      if (!success) {
        setIsStarFilled(false);
      }
    }
  };

  // Handle unfavorite confirmation
  const handleConfirmUnfavourite = async () => {
    if (pendingFavoriteAction && pendingFavoriteAction.action === 'unfavorite') {
      setIsStarFilled(false);
      if (onToggleFavorite) {
        onToggleFavorite();
      }
      const success = await toggleFavorite(pendingFavoriteAction.cardData.id, pendingFavoriteAction.cardData.title);
      if (!success) {
        setIsStarFilled(true);
      }
    }
    setShowUnfavouriteModal(false);
    setPendingFavoriteAction(null);
  };

  // Handle unfavorite cancellation
  const handleCancelUnfavourite = () => {
    setShowUnfavouriteModal(false);
    setPendingFavoriteAction(null);
  };

  if (!cardData) return null;

  // Sort chart data ascending by date (do not mutate original prop)
  const sortedChartData = [...cardData.chartData].sort((a, b) => {
    const ta = new Date(a.date).getTime();
    const tb = new Date(b.date).getTime();
    return ta - tb;
  });

  // Compute max value from data, actual, and target — scaled 10% above for headroom
  const dataMax = Math.max(...sortedChartData.map(d => d.value), cardData.actual, cardData.target);
  const maxChartValue = dataMax > 0 ? dataMax * 1.1 : 1;

  const isDeltaNegative = cardData.delta < 0;
  const isDeltaPositive = cardData.delta > 0;
  const isDeltaBad = cardData.invertDeltaColor ? isDeltaPositive : isDeltaNegative;
  const isDeltaGood = cardData.invertDeltaColor ? isDeltaNegative : isDeltaPositive;
  const deltaArrowColor = isDeltaBad ? '#C12C01' : 'rgb(18, 159, 20)';

  return (
    <LHOverlay visible={visible} onDismiss={onDismiss} title="">
      {tooltip}
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.modalContent}>
          {/* Header with Title and Favorite */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={[styles.badge, { backgroundColor: cardData.badgeColor || '#D4F4DD' }]}>
                <Text style={styles.badgeText}>{cardData.badgeLetter}</Text>
              </View>
              <Text style={styles.title}>{cardData.title}</Text>
            </View>
            <TouchableOpacity 
              style={[
                styles.favoriteButton,
                Platform.OS === 'web' && ({
                  cursor: (isLoading || isMandatory) ? 'not-allowed' : 'pointer',
                } as any),
              ]}
              onPress={handleStarPress}
              disabled={isLoading || isMandatory}
            >
              {isMandatory ? (
                <View style={styles.starIconContainer}>
                  <MaterialIcons name="lock" size={18} color="#616161" />
                </View>
              ) : (
                <StarIcon filled={isStarFilled} isLoading={isLoading} />
              )}
            </TouchableOpacity>
          </View>

          {/* Metrics Container */}
          <View style={styles.metricsContainer}>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Actual</Text>
              <Text style={styles.metricValue}>{cardData.actual.toFixed(2)}</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Target</Text>
              <Text style={styles.metricValue}>{cardData.target.toFixed(2)}</Text>
            </View>
            <View style={styles.metricCard}>
              <Text style={styles.metricLabel}>Delta</Text>
              <View style={styles.deltaContainer}>
                {isDeltaNegative && <DownArrowIcon color={deltaArrowColor} />}
                {isDeltaPositive && <UpArrowIcon color={deltaArrowColor} />}
                <Text style={[styles.metricValue, isDeltaBad && styles.deltaValueRed, isDeltaGood && styles.deltaValueGreen]}>
                  {Math.abs(cardData.delta).toFixed(2)}
                </Text>
              </View>
            </View>
          </View>

          {/* Chart Section */}
          <View style={styles.chartSection}>
            <View style={styles.chartContainer}>
              {/* Y-axis */}
              <View style={styles.yAxisContainer}>
                <Text style={styles.yAxisLabel}>
                  {Number.isInteger(maxChartValue) ? maxChartValue : maxChartValue.toFixed(2)}
                </Text>
                <View style={styles.yAxisLine} />
                <Text style={styles.yAxisLabel}>0</Text>
              </View>

              {/* Chart Content */}
              <View style={styles.chartContent}>
                <View style={styles.barsContainer}>
                  {sortedChartData.map((dataPoint, index) => {
                    const barHeight = (dataPoint.value / maxChartValue) * 200;
                    return (
                      <View key={index} style={styles.barWrapper} {...getBarProps(dataPoint)}>
                        <View style={styles.barContainer}>
                          <View
                            style={[
                              styles.bar,
                              {
                                height: barHeight,
                                backgroundColor: '#41B8E6',
                              },
                            ]}
                          />
                        </View>
                      </View>
                    );
                  })}
                </View>

                {/* X-axis labels */}
                <View style={styles.xAxisContainer}>
                  {sortedChartData.map((dataPoint, index) => (
                    <View key={index} style={styles.xAxisLabelWrapper}>
                      <Text style={styles.xAxisLabel}>{dataPoint.date}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </View>

          {/* Action Buttons */}
          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={[styles.button, styles.backButton]}
              onPress={onDismiss}
              activeOpacity={0.7}
            >
              <Text style={styles.backButtonText}>Back</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.button, styles.actionButton]}
              onPress={onCreateActionPlan}
              activeOpacity={0.7}
            >
              <Text style={styles.actionButtonText}>Create Action Plan</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      {/* Unfavourite Confirmation Modal */}
      <LHModalPop
        visible={showUnfavouriteModal}
        onClose={handleCancelUnfavourite}
        title={getUnfavouriteModalConfig('review-performance').header}
        showFooter={true}
        primaryButtonText={getUnfavouriteModalConfig('review-performance').primaryButton}
        secondaryButtonText={getUnfavouriteModalConfig('review-performance').secondaryButton}
        onPrimaryPress={handleConfirmUnfavourite}
        onSecondaryPress={handleCancelUnfavourite}
        showCloseButton={true}
      >
        <View style={{ gap: 12, paddingVertical: 8 }}>
          <Text style={{ fontSize: 14, color: '#3A3A3A', fontFamily: FontFamily.regular, lineHeight: 20 }}>
            {getUnfavouriteModalConfig('review-performance').bodyLine1}
          </Text>
          <Text style={{ fontSize: 14, color: '#616161', fontFamily: FontFamily.regular, lineHeight: 20 }}>
            {getUnfavouriteModalConfig('review-performance').bodyLine2}
          </Text>
        </View>
      </LHModalPop>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  modalContent: {
    padding: 24,
    backgroundColor: '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  badge: {
    width: 32,
    height: 32,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1B5E20',
    fontFamily: FontFamily.regular,
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    color: '#2D2D2D',
    fontFamily: FontFamily.medium,
    flex: 1,
  },
  favoriteButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  starIconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  starIcon: {
    fontSize: 18,
    color: '#616161',
  },
  downArrowContainer: {
    marginRight: 4,
  },
  downArrow: {
    fontSize: 8,
    color: '#C12C01',
  },
  upArrowContainer: {
    marginRight: 4,
  },
  upArrow: {
    fontSize: 8,
    color: 'rgb(18, 159, 20)',
  },
  metricsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E3F2FD',
    paddingVertical: 12,
    paddingHorizontal: 2,
    marginBottom: 32,
    flexWrap: 'wrap',
  },
  metricCard: {
    flex: 1,
    alignItems: 'center',
  },
  metricLabel: {
    fontSize: 14,
    fontWeight: '400',
    color: '#4B4B4B',
    marginBottom: 8,
    fontFamily: FontFamily.regular,
  },
  metricValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000000',
    fontFamily: FontFamily.regular,
  },
  deltaContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deltaValueRed: {
    color: '#D32F2F',
  },
  deltaValueGreen: {
    color: 'rgb(18, 159, 20)',
  },
  chartSection: {
    marginBottom: 0,
  },
  chartContainer: {
    flexDirection: 'row',
    gap: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    padding: 16,
  },
  yAxisContainer: {
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 22,
    marginTop: -6,
  },
  yAxisLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  yAxisLine: {
    flex: 1,
  },
  chartContent: {
    flex: 1,
  },
  barsContainer: {
    flexDirection: 'row',
    height: 200,
    alignItems: 'flex-end',
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    gap: 4,
  },
  barWrapper: {
    flex: 1,
    alignItems: 'center',
  },
  barContainer: {
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  bar: {
    width: '70%',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    minHeight: 2,
  },
  xAxisContainer: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 4,
  },
  xAxisLabelWrapper: {
    flex: 1,
    alignItems: 'center',
  },
  xAxisLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 24,
    paddingTop: 0,
  },
  button: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 9999,
    minWidth: 120,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backButton: {
    backgroundColor: 'transparent',
    borderWidth: 0,
  },
  actionButton: {
    backgroundColor: '#004895',
  },
  backButtonText: {
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 20,
    color: '#004895',
  },
  actionButtonText: {
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
  },
});

export default SparcKpiDetailModal;
